import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useActivityLog } from '../hooks/useActivityLog';
import { cn } from '../lib/utils';
import { motion, AnimatePresence } from 'framer-motion';
import api from '../lib/api';
import ReminderPreview from './ReminderPreview';
import { todayISO } from '../lib/dateLocal';
import {
  Bell, X, Plus, Check, Trash2, Phone, Mail, CheckSquare,
  Users, Clock, Building2, ChevronDown
} from 'lucide-react';

const REMINDER_TYPES = [
  { value: 'poziv', label: 'Poziv', icon: Phone, color: 'emerald' },
  { value: 'email', label: 'Email', icon: Mail, color: 'sky' },
  { value: 'zadatak', label: 'Zadatak', icon: CheckSquare, color: 'amber' },
  { value: 'sastanak', label: 'Sastanak', icon: Users, color: 'violet' },
];

function pad2(n) { return String(n).padStart(2, '0'); }
function getDefaultDateTime() {
  const d = new Date();
  d.setHours(d.getHours() + 1, 0, 0, 0);
  // Lokalno vrijeme (ne UTC!) — konzistentno s presetima i backend usporedbom.
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}T${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}
function localTodayStr() {
  const d = new Date();
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

export default function ReminderWidget() {
  const { user } = useAuth();
  const location = useLocation();
  const logActivity = useActivityLog();
  const [open, setOpen] = useState(false);
  const [reminders, setReminders] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(false);

  // Form state
  const [text, setText] = useState('');
  const [remindAt, setRemindAt] = useState(getDefaultDateTime());
  const [reminderType, setReminderType] = useState('zadatak');
  const [companyName, setCompanyName] = useState('');
  const [companyLocked, setCompanyLocked] = useState(false);
  const [companySuggestions, setCompanySuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [assignedTo, setAssignedTo] = useState('');
  const [showForm, setShowForm] = useState(true);

  // ─── ALERT POPUP (firing reminder) ───
  // Kad podsjetnik dospije (remind_at <= now), iskoči obavezni popup s zvukom.
  // User mora kliknuti gumb (Završeno / Odgodi / Zatvori) da se ukloni.
  // ACK lista u sessionStorage — sprječava re-popup za isti ID dok je tab otvoren.
  const [firedAlert, setFiredAlert] = useState(null); // {reminder} ili null
  const [previewReminder, setPreviewReminder] = useState(null); // puni pregled na klik u listi
  const [snoozing, setSnoozing] = useState(false);
  const ackedRef = useRef(new Set(
    JSON.parse(sessionStorage.getItem('reminder-acked-ids') || '[]')
  ));
  const persistAcked = (id) => {
    ackedRef.current.add(id);
    sessionStorage.setItem('reminder-acked-ids', JSON.stringify([...ackedRef.current]));
  };

  // Web Audio chime — dvotonski (880 → 660 Hz, 0.5s) — pleasant attention sound.
  // Browser autoplay policy: AudioContext radi tek nakon user-interakcije na page-u.
  // Korisnik je do sad sigurno kliknuo nešto (login, gumb), pa će zvuk raditi.
  const playChime = useCallback(() => {
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) return;
      const ctx = new AC();
      // 2 tona (ding-dong) — sliče Mac notification sound-u
      [{ f: 880, t: 0 }, { f: 660, t: 0.18 }, { f: 880, t: 0.36 }].forEach(({ f, t }) => {
        const o = ctx.createOscillator();
        const g = ctx.createGain();
        o.connect(g); g.connect(ctx.destination);
        o.type = 'sine';
        o.frequency.value = f;
        const start = ctx.currentTime + t;
        g.gain.setValueAtTime(0.0001, start);
        g.gain.exponentialRampToValueAtTime(0.25, start + 0.02);
        g.gain.exponentialRampToValueAtTime(0.0001, start + 0.15);
        o.start(start);
        o.stop(start + 0.18);
      });
      setTimeout(() => { try { ctx.close(); } catch {} }, 800);
    } catch (e) { /* tih fail — bolje nego da popup ne radi */ }
  }, []);

  // Auto-detect company from URL
  useEffect(() => {
    const path = location.pathname;
    const params = new URLSearchParams(location.search);
    if (path.startsWith('/leads/') && path !== '/leads') {
      const name = decodeURIComponent(path.split('/leads/')[1]);
      if (name) { setCompanyName(name); setCompanyLocked(true); }
    } else if (path === '/crm' && params.get('company')) {
      setCompanyName(decodeURIComponent(params.get('company'))); setCompanyLocked(true);
    } else {
      setCompanyName(''); setCompanyLocked(false);
    }
  }, [location]);

  // Company autocomplete — search after 3 chars
  const searchCompanies = async (q) => {
    if (q.length < 3) { setCompanySuggestions([]); setShowSuggestions(false); return; }
    try {
      const { data } = await api.get('/leads/search-companies', { params: { q } });
      setCompanySuggestions(data || []);
      setShowSuggestions((data || []).length > 0);
    } catch { setCompanySuggestions([]); }
  };

  // Podsjetnici — ovo se polla svakih 20 s.
  const loadData = useCallback(async () => {
    try {
      const { data } = await api.get('/leads/reminders');
      setReminders(data || []);
    } catch {}
  }, []);

  // Tim se učitava JEDNOM — prije je išao uz svaki tick (svakih 20 s po korisniku),
  // a popis članova tima se praktički ne mijenja. Nepotrebno opterećenje backenda.
  useEffect(() => {
    api.get('/auth/team').then(r => setTeamMembers(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  // ─── Polling za dospjele reminder-e (svakih 20s, samo kad je tab vidljiv) ───
  useEffect(() => {
    if (!user) return;
    const tick = () => {
      if (document.visibilityState !== 'visible') return;
      loadData();
    };
    const id = setInterval(tick, 20000);
    // Refresh i odmah kad tab postane vidljiv (npr. user vrati fokus na karticu)
    const onVisible = () => { if (document.visibilityState === 'visible') loadData(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => { clearInterval(id); document.removeEventListener('visibilitychange', onVisible); };
  }, [user, loadData]);

  // ─── Trigger popup kad imamo dospjeli reminder koji nije već ack-an ───
  useEffect(() => {
    if (firedAlert) return; // već prikazujemo jedan — ne otvaraj drugi
    const now = new Date();
    // Filter: aktivni (!done), istekli (remind_at <= now), nisu ack-ani u ovoj sesiji,
    // pripadaju trenutnom useru (assigned_to == me ILI created_by == me ako nije zadužen).
    const due = reminders
      .filter(r => !r.done)
      .filter(r => new Date(r.remind_at) <= now)
      .filter(r => !ackedRef.current.has(r.reminder_id))
      .filter(r => {
        const me = user?.username;
        if (!me) return false;
        if (r.assigned_to && r.assigned_to !== '') return r.assigned_to === me;
        return r.created_by === me;
      })
      .sort((a, b) => new Date(a.remind_at) - new Date(b.remind_at));
    if (due.length > 0) {
      setFiredAlert(due[0]);
      playChime();
    }
  }, [reminders, firedAlert, user, playChime]);

  const handleAlertDone = async () => {
    if (!firedAlert) return;
    const id = firedAlert.reminder_id;
    persistAcked(id);
    setFiredAlert(null);
    await handleDoneRef.current(id);
  };

  const handleAlertSnooze = async (mins) => {
    if (!firedAlert) return;
    setSnoozing(true);
    const id = firedAlert.reminder_id;
    persistAcked(id); // ne pokaže ponovo dok backend ne potvrdi novi remind_at
    const newTime = new Date(Date.now() + mins * 60 * 1000).toISOString();
    try {
      await api.put(`/leads/reminders/${id}/snooze`, { remind_at: newTime });
      // Lokalno ažuriraj remind_at i ukloni iz ack-a (kad nova time dođe, opet smije fire-ati)
      ackedRef.current.delete(id);
      sessionStorage.setItem('reminder-acked-ids', JSON.stringify([...ackedRef.current]));
      setReminders(prev => prev.map(r => r.reminder_id === id ? { ...r, remind_at: newTime } : r));
    } catch (e) {
      // Fallback: ako snooze endpoint ne postoji, samo dismissaj i info usera
      console.warn('Snooze endpoint failed, fallback to dismiss', e);
    }
    setFiredAlert(null);
    setSnoozing(false);
  };

  const handleAlertDismiss = () => {
    if (!firedAlert) return;
    persistAcked(firedAlert.reminder_id);
    setFiredAlert(null);
  };

  // ref na handleDone — definira se ispod, ali useEffect ga može koristiti
  const handleDoneRef = useRef(null);

  // Refresh when opening + show form
  useEffect(() => { if (open) { loadData(); setShowForm(true); } }, [open, loadData]);

  const activeReminders = reminders.filter(r => !r.done).sort((a, b) => new Date(a.remind_at) - new Date(b.remind_at));
  const doneReminders = reminders.filter(r => r.done).slice(0, 5);

  const handleAdd = async () => {
    if (!text.trim() || !remindAt) return;
    // Podsjetnik SAMO u budućnost — najranije sada. Sprječava prazne/prošle (bug 01.01.1970) i besmislene alarme.
    if (isNaN(new Date(remindAt).getTime()) || new Date(remindAt).getTime() <= Date.now()) {
      alert('Podsjetnik se može zadati samo za buduće vrijeme.\nOdaberi datum i sat koji su ispred trenutnog vremena.');
      return;
    }
    setLoading(true);
    try {
      const { data: created } = await api.post('/leads/reminders', {
        company_name: companyName || '—',
        text: text.trim(),
        remind_at: remindAt,
        assigned_to: assignedTo || user?.username || '',
        reminder_type: reminderType,
      });
      logActivity('reminder_created', { company: companyName, type: reminderType });
      // Obavijesti otvoreni profil/CRM detalj da odmah pokaže novi podsjetnik u kartici
      // (bez ovoga kartica se osvježi tek na ponovno otvaranje firme).
      window.dispatchEvent(new CustomEvent('reminder-added', { detail: {
        reminder_id: created?.reminder_id, company_name: companyName || '—', text: text.trim(),
        remind_at: remindAt, assigned_to: assignedTo || user?.username || '',
        created_by: user?.username || '', reminder_type: reminderType, done: false,
      } }));
      setText('');
      setRemindAt(getDefaultDateTime());
      setReminderType('zadatak');
      setAssignedTo('');
      setShowForm(false);
      await loadData();
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const handleDone = async (id) => {
    try {
      await api.put(`/leads/reminders/${id}`);
      logActivity('reminder_done', { reminder_id: id });
      setReminders(prev => prev.map(r => r.reminder_id === id ? { ...r, done: true } : r));
    } catch (e) { console.error(e); }
  };
  // Sinkroniziraj ref da useEffect (gore) može pozvati novu verziju nakon hooks rerender-a
  useEffect(() => { handleDoneRef.current = handleDone; });

  const handleDelete = async (id) => {
    try {
      await api.delete(`/leads/reminders/${id}`);
      setReminders(prev => prev.filter(r => r.reminder_id !== id));
    } catch (e) { console.error(e); }
  };

  const getTypeConfig = (type) => REMINDER_TYPES.find(t => t.value === type) || REMINDER_TYPES[2];

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    const now = new Date();
    const isToday = d.toDateString() === now.toDateString();
    const tomorrow = new Date(now); tomorrow.setDate(tomorrow.getDate() + 1);
    const isTomorrow = d.toDateString() === tomorrow.toDateString();
    const time = d.toLocaleTimeString('hr-HR', { hour: '2-digit', minute: '2-digit' });
    if (isToday) return `Danas ${time}`;
    if (isTomorrow) return `Sutra ${time}`;
    return d.toLocaleDateString('hr-HR', { day: '2-digit', month: '2-digit' }) + ` ${time}`;
  };

  const isOverdue = (dateStr) => {
    if (!dateStr) return false;
    return new Date(dateStr) < new Date();
  };

  if (!user) return null;

  return (
    <>
      {/* Floating button — amber bell, always visible */}
      <button onClick={() => setOpen(v => !v)}
        className={cn(
          "fixed z-50 rounded-2xl flex items-center gap-2 shadow-lg transition-all hover:scale-105 px-4 py-3",
          open ? "bg-stone-800 text-white" : "bg-amber-500 text-white hover:bg-amber-600"
        )}
        style={{ bottom: '88px', right: '24px' }}>
        {open ? <X size={18} /> : <Bell size={18} />}
        {!open && <span className="text-[12px] font-bold">Podsjetnik</span>}
        {!open && activeReminders.length > 0 && (
          <span className="w-5 h-5 rounded-full bg-rose-500 text-white text-[10px] font-bold flex items-center justify-center">
            {activeReminders.length}
          </span>
        )}
      </button>

      {/* Panel */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="fixed z-50 bg-white rounded-xl shadow-2xl border border-stone-200 flex flex-col"
            style={{ bottom: '148px', right: '24px', width: '380px', maxHeight: 'min(85vh, 720px)', overflow: 'hidden' }}
          >
            {/* Header — fiksiran, ne sudjeluje u flex shrink */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-stone-100" style={{ flexShrink: 0 }}>
              <div className="flex items-center gap-2">
                <Bell size={16} className="text-amber-500" />
                <span className="font-bold text-[14px]">Podsjetnici</span>
                {activeReminders.length > 0 && (
                  <span className="w-5 h-5 rounded-full bg-amber-500 text-white text-[10px] font-bold flex items-center justify-center">{activeReminders.length}</span>
                )}
              </div>
              <button onClick={() => setShowForm(v => !v)}
                className={cn("w-8 h-8 rounded-lg flex items-center justify-center transition-all",
                  showForm ? "bg-stone-100 text-stone-500" : "bg-amber-50 text-amber-600 hover:bg-amber-100")}>
                {showForm ? <ChevronDown size={16} /> : <Plus size={16} />}
              </button>
            </div>

            {/* Quick-add form */}
            <AnimatePresence>
              {showForm && (
                <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }}
                  className="overflow-hidden border-b border-stone-100"
                  style={{ flexShrink: 0 }}>
                  {/* Scrollable form fields — submit button je izvan da je uvijek vidljiv */}
                  <div className="px-4 pt-4 pb-2 space-y-3 bg-stone-50/50"
                       style={{ maxHeight: 'calc(min(85vh, 720px) - 180px)', overflowY: 'auto' }}>
                    {/* Text */}
                    <input value={text} onChange={e => setText(e.target.value)} placeholder="Što trebaš napraviti?"
                      className="w-full h-10 px-3 rounded-lg border border-stone-200 text-[13px] focus:border-amber-400 focus:ring-2 focus:ring-amber-400/20 outline-none bg-white"
                      onKeyDown={e => { if (e.key === 'Enter' && text.trim()) handleAdd(); }} autoFocus />

                    {/* Type selector */}
                    <div className="flex gap-1">
                      {REMINDER_TYPES.map(t => {
                        const Icon = t.icon;
                        const active = reminderType === t.value;
                        return (
                          <button key={t.value} onClick={() => setReminderType(t.value)}
                            className={cn("flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-[11px] font-bold transition-all border",
                              active ? `bg-${t.color}-50 text-${t.color}-700 border-${t.color}-300` : "bg-white text-stone-400 border-stone-200 hover:border-stone-300")}>
                            <Icon size={13} /> {t.label}
                          </button>
                        );
                      })}
                    </div>

                    {/* Datum + Sat (zasebno radi jasnoće — Safari/Firefox skrivaju vrijeme u datetime-local) */}
                    <div>
                      <div className="text-[10px] font-bold uppercase tracking-wider text-stone-500 mb-1.5">Kada me podsjetiti</div>
                      <div className="grid grid-cols-[1.3fr_0.9fr] gap-2">
                        <div className="relative">
                          <label className="absolute -top-2 left-2 px-1 bg-stone-50 text-[9px] font-bold text-stone-500 uppercase tracking-wider">Datum</label>
                          <input
                            type="date"
                            min={localTodayStr()}
                            value={(remindAt || '').slice(0, 10)}
                            onChange={e => {
                              const newDate = e.target.value;
                              const time = (remindAt || '').slice(11, 16) || '09:00';
                              setRemindAt(newDate ? `${newDate}T${time}` : '');
                            }}
                            className="w-full h-10 px-2 rounded-lg border border-stone-200 text-[13px] font-mono focus:border-amber-400 outline-none bg-white"
                          />
                        </div>
                        <div className="relative">
                          <label className="absolute -top-2 left-2 px-1 bg-stone-50 text-[9px] font-bold text-stone-500 uppercase tracking-wider">Sat ⏰</label>
                          <input
                            type="time"
                            step="300"
                            value={(remindAt || '').slice(11, 16) || ''}
                            onChange={e => {
                              const newTime = e.target.value;
                              const date = (remindAt || '').slice(0, 10) || todayISO();
                              setRemindAt(newTime ? `${date}T${newTime}` : date);
                            }}
                            className="w-full h-10 px-2 rounded-lg border border-stone-200 text-[14px] font-mono font-bold text-stone-800 focus:border-amber-400 outline-none bg-white text-center"
                          />
                        </div>
                      </div>
                      {/* Quick presets za vrijeme */}
                      <div className="flex gap-1 mt-2 flex-wrap">
                        {[
                          { label: '+15 min', mins: 15 },
                          { label: '+1h', mins: 60 },
                          { label: '+2h', mins: 120 },
                          { label: 'Sutra 09:00', set: 'tomorrow9' },
                          { label: 'Sutra 14:00', set: 'tomorrow14' },
                        ].map(p => (
                          <button
                            key={p.label}
                            onClick={() => {
                              const d = new Date();
                              if (p.mins) d.setMinutes(d.getMinutes() + p.mins);
                              else if (p.set === 'tomorrow9') { d.setDate(d.getDate() + 1); d.setHours(9, 0, 0, 0); }
                              else if (p.set === 'tomorrow14') { d.setDate(d.getDate() + 1); d.setHours(14, 0, 0, 0); }
                              // YYYY-MM-DDTHH:MM (lokalno vrijeme, ne UTC)
                              const pad = (n) => String(n).padStart(2, '0');
                              setRemindAt(`${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`);
                            }}
                            className="px-2 py-1 rounded-md bg-amber-50 hover:bg-amber-100 border border-amber-200 text-[10px] font-bold text-amber-800 transition-colors"
                          >{p.label}</button>
                        ))}
                      </div>
                      {/* Jasno istaknut prikaz KADA se podsjetnik zadaje (+ upozorenje ako prošlo) */}
                      {remindAt && (() => {
                        const t = new Date(remindAt).getTime();
                        const bad = isNaN(t) || t <= Date.now();
                        return (
                          <div className="mt-2 flex items-center gap-2 px-3 py-2 rounded-lg"
                            style={{ background: bad ? '#FEE2E2' : '#FEF3C7', border: `1px solid ${bad ? '#FCA5A5' : '#FCD34D'}` }}>
                            <span className="text-[13px]">⏰</span>
                            <span className="text-[12px] font-bold" style={{ color: bad ? '#B91C1C' : '#92400E' }}>
                              {isNaN(t) ? 'Neispravan datum' : new Date(remindAt).toLocaleString('hr-HR', { weekday: 'long', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                            </span>
                            {!isNaN(t) && t <= Date.now() && <span className="ml-auto text-[10px] font-extrabold" style={{ color: '#B91C1C' }}>⚠ PROŠLO</span>}
                          </div>
                        );
                      })()}
                    </div>

                    {/* Zaduži */}
                    <div className="relative">
                      <label className="absolute -top-2 left-2 px-1 bg-stone-50 text-[9px] font-bold text-stone-500 uppercase tracking-wider">Zaduži</label>
                      <select value={assignedTo} onChange={e => setAssignedTo(e.target.value)}
                        className="w-full h-10 px-2 rounded-lg border border-stone-200 text-[12px] font-bold focus:border-amber-400 outline-none bg-white">
                        <option value="">Zaduži meni</option>
                        {teamMembers.map(m => (
                          <option key={m.email} value={m.email}>{m.name || m.email?.split('@')[0]}</option>
                        ))}
                      </select>
                    </div>

                    {/* Company — autocomplete with suggestions */}
                    <div style={{ position: 'relative' }}>
                      {companyLocked ? (
                        <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-amber-50 border border-amber-200 text-[12px]">
                          <Building2 size={13} className="text-amber-600 shrink-0" />
                          <span className="font-bold text-amber-800 truncate">{companyName}</span>
                          <button onClick={() => { setCompanyName(''); setCompanyLocked(false); }} className="ml-auto text-amber-400 hover:text-amber-600"><X size={12} /></button>
                        </div>
                      ) : (
                        <input value={companyName}
                          onChange={e => { setCompanyName(e.target.value); searchCompanies(e.target.value); }}
                          onFocus={() => { if (companySuggestions.length > 0) setShowSuggestions(true); }}
                          onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                          placeholder="Firma (upiši min. 3 slova za pretragu)"
                          className="w-full h-9 px-3 rounded-lg border border-stone-200 text-[12px] focus:border-amber-400 outline-none bg-white" />
                      )}
                      {showSuggestions && companySuggestions.length > 0 && (
                        <div style={{
                          position: 'absolute', top: '100%', left: 0, right: 0, marginTop: '4px',
                          background: '#FFFFFF', borderRadius: '8px', border: '1px solid #E8E5E0',
                          boxShadow: '0 4px 16px rgba(120,100,60,0.15)', zIndex: 60, maxHeight: '180px', overflow: 'auto',
                        }}>
                          {companySuggestions.map((name, i) => (
                            <button key={i}
                              onMouseDown={(e) => { e.preventDefault(); setCompanyName(name); setCompanyLocked(true); setShowSuggestions(false); }}
                              style={{
                                display: 'flex', alignItems: 'center', gap: '8px', width: '100%',
                                padding: '8px 12px', border: 'none', cursor: 'pointer', textAlign: 'left',
                                fontFamily: 'inherit', fontSize: '12px', fontWeight: 500, color: '#1C1917',
                                background: 'transparent',
                              }}
                              onMouseEnter={e => e.currentTarget.style.background = '#FAFAF8'}
                              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                            >
                              <Building2 size={12} style={{ color: '#A8A29E', flexShrink: 0 }} />
                              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Submit — fiksni footer forme, UVIJEK vidljiv bez obzira na duljinu liste/forme */}
                  <div className="px-4 py-3 bg-stone-50/50 border-t border-stone-100" style={{ flexShrink: 0 }}>
                    <button onClick={handleAdd} disabled={!text.trim() || loading}
                      className="w-full h-10 rounded-lg gradient-gold text-white text-[13px] font-bold hover:opacity-90 disabled:opacity-50 flex items-center justify-center gap-2 shadow-sm">
                      {loading ? <Clock size={14} className="animate-spin" /> : <Plus size={14} />}
                      Dodaj podsjetnik
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Reminder list */}
            <div className="flex-1 overflow-auto">
              {activeReminders.length === 0 && doneReminders.length === 0 ? (
                <div className="text-center py-8">
                  <Bell size={28} className="mx-auto mb-2 text-stone-200" />
                  <p className="text-[13px] text-stone-400">Nema podsjetnika</p>
                  <button onClick={() => setShowForm(true)} className="mt-2 text-[12px] font-bold text-amber-600 hover:text-amber-700">+ Dodaj prvi</button>
                </div>
              ) : (
                <div className="divide-y divide-stone-100">
                  {activeReminders.map(r => {
                    const config = getTypeConfig(r.reminder_type);
                    const Icon = config.icon;
                    const overdue = isOverdue(r.remind_at);
                    // Klik na podsjetnik → PUNI PREGLED (cijeli tekst + firma + Uredi)
                    return (
                      <div key={r.reminder_id} onClick={() => setPreviewReminder(r)}
                        title="Klikni za puni pregled i uređivanje"
                        className={cn("flex items-start gap-3 px-4 py-3 hover:bg-stone-50/50 transition-colors group cursor-pointer",
                        overdue && "bg-rose-50/30")}>
                        <div className={cn("w-8 h-8 rounded-lg flex items-center justify-center shrink-0 mt-0.5",
                          overdue ? "bg-rose-100 text-rose-600" : `bg-${config.color}-50 text-${config.color}-600`)}>
                          <Icon size={14} />
                        </div>
                        <div className="flex-1 min-w-0">
                          {/* PUNO ime firme — svoj red, jasno vidljivo (prije: 2 riječi u sitnom) */}
                          {r.company_name && r.company_name !== '—' && (
                            <p className="text-[11px] font-bold truncate" style={{ color: '#C17F0A', marginBottom: '1px' }}>{r.company_name}</p>
                          )}
                          <p className="text-[13px] font-medium text-stone-700 leading-snug" style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{r.text}</p>
                          <div className="flex items-center gap-2 mt-1 text-[10px] text-stone-400">
                            <span className={cn("font-bold", overdue ? "text-rose-500" : "text-stone-500")}>
                              {formatDate(r.remind_at)}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                          <button onClick={(e) => { e.stopPropagation(); handleDone(r.reminder_id); }} title="Završi"
                            className="w-7 h-7 rounded-lg flex items-center justify-center text-emerald-500 hover:bg-emerald-50 transition-colors">
                            <Check size={14} />
                          </button>
                          <button onClick={(e) => { e.stopPropagation(); handleDelete(r.reminder_id); }} title="Obriši"
                            className="w-7 h-7 rounded-lg flex items-center justify-center text-stone-300 hover:text-rose-500 hover:bg-rose-50 transition-colors">
                            <Trash2 size={12} />
                          </button>
                        </div>
                      </div>
                    );
                  })}

                  {/* Done section */}
                  {doneReminders.length > 0 && (
                    <>
                      <div className="px-4 py-2 bg-stone-50">
                        <span className="text-[10px] font-bold text-stone-400 uppercase tracking-wider">Završeni</span>
                      </div>
                      {doneReminders.map(r => (
                        <div key={r.reminder_id} className="flex items-center gap-3 px-4 py-2 opacity-50">
                          <Check size={14} className="text-emerald-400 shrink-0" />
                          <span className="text-[12px] text-stone-400 line-through truncate">{r.text}</span>
                          <button onClick={() => handleDelete(r.reminder_id)} className="ml-auto text-stone-300 hover:text-rose-400">
                            <Trash2 size={11} />
                          </button>
                        </div>
                      ))}
                    </>
                  )}
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ─── PUNI PREGLED PODSJETNIKA (klik u listi) ─── */}
      {previewReminder && (
        <ReminderPreview reminder={previewReminder} teamMembers={teamMembers}
          onClose={() => setPreviewReminder(null)}
          onChanged={() => loadData()} />
      )}

      {/* ─── FIRED REMINDER POPUP (obavezna potvrda + zvuk) ─── */}
      <AnimatePresence>
        {firedAlert && (() => {
          const cfg = getTypeConfig(firedAlert.reminder_type);
          const Icon = cfg.icon;
          return (
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 z-[9999] flex items-center justify-center"
              style={{ background: 'rgba(28,25,23,0.55)', backdropFilter: 'blur(4px)' }}
              // backdrop click ne zatvara — user MORA odabrati akciju
              onClick={(e) => e.stopPropagation()}
            >
              <motion.div
                initial={{ scale: 0.9, y: 20 }}
                animate={{ scale: 1, y: 0 }}
                exit={{ scale: 0.95, y: 10 }}
                transition={{ type: 'spring', damping: 18, stiffness: 280 }}
                className="bg-white rounded-2xl shadow-2xl border border-stone-200"
                style={{ width: '460px', maxWidth: '92vw', overflow: 'hidden' }}
              >
                {/* Pulsing header */}
                <div style={{
                  padding: '20px 24px 18px',
                  background: 'linear-gradient(135deg, #FFF4DB 0%, #FFE5A8 100%)',
                  borderBottom: '1px solid #E8D4A8',
                  display: 'flex', alignItems: 'center', gap: 14,
                }}>
                  <motion.div
                    animate={{ scale: [1, 1.12, 1] }}
                    transition={{ duration: 1.2, repeat: Infinity }}
                    style={{
                      width: 48, height: 48, borderRadius: '50%',
                      background: 'linear-gradient(135deg, #C17F0A, #8B5A07)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      boxShadow: '0 4px 16px rgba(193,127,10,0.4)',
                    }}>
                    <Bell size={22} className="text-white" />
                  </motion.div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: '#78510A', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                      ⏰ Podsjetnik dospio
                    </div>
                    <div style={{ fontSize: 16, fontWeight: 800, color: '#1C1917', marginTop: 2, letterSpacing: '-0.01em' }}>
                      {formatDate(firedAlert.remind_at)}
                    </div>
                  </div>
                </div>

                {/* Content */}
                <div style={{ padding: '20px 24px' }}>
                  {/* Type + company */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
                    <span style={{
                      display: 'inline-flex', alignItems: 'center', gap: 5,
                      padding: '4px 10px', borderRadius: '999px',
                      background: '#F5F3EF', color: '#57534E',
                      fontSize: 11, fontWeight: 700,
                    }}>
                      <Icon size={12} /> {cfg.label}
                    </span>
                    {firedAlert.company_name && (
                      <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: 5,
                        padding: '4px 10px', borderRadius: '999px',
                        background: '#FFF4DB', color: '#78510A', border: '1px solid #F5D78A',
                        fontSize: 11, fontWeight: 700,
                        maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }}>
                        <Building2 size={12} /> {firedAlert.company_name}
                      </span>
                    )}
                  </div>
                  {/* Reminder text */}
                  <div style={{
                    fontSize: 15, fontWeight: 600, color: '#1C1917',
                    lineHeight: 1.45, padding: '12px 14px',
                    background: '#FAFAF8', border: '1px solid #E8E5E0', borderRadius: 10,
                  }}>
                    {firedAlert.text || '(bez teksta)'}
                  </div>
                  {firedAlert.created_by && firedAlert.created_by !== user?.username && (
                    <div style={{ fontSize: 11, color: '#A8A29E', marginTop: 8 }}>
                      Zadužio te: <b style={{ color: '#57534E' }}>{firedAlert.created_by.split('@')[0]}</b>
                    </div>
                  )}
                </div>

                {/* Action buttons — UVIJEK vidljivi, MORA se kliknuti */}
                <div style={{ padding: '14px 24px 20px', display: 'flex', gap: 8, flexWrap: 'wrap', borderTop: '1px solid #F0EDE8' }}>
                  <button
                    onClick={handleAlertDone}
                    disabled={snoozing}
                    style={{
                      flex: '1 1 140px', padding: '11px 14px', borderRadius: 10,
                      background: 'linear-gradient(135deg, #15803D, #166534)',
                      border: 'none', color: '#FFFFFF', cursor: 'pointer',
                      fontSize: 13, fontWeight: 700, fontFamily: 'inherit',
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                      boxShadow: '0 2px 8px rgba(21,128,61,0.3)',
                    }}>
                    <Check size={14} /> Završeno
                  </button>
                  <button
                    onClick={() => handleAlertSnooze(15)}
                    disabled={snoozing}
                    style={{
                      flex: '1 1 100px', padding: '11px 14px', borderRadius: 10,
                      background: '#FFFFFF', border: '1.5px solid #E8D4A8',
                      color: '#78510A', cursor: 'pointer',
                      fontSize: 13, fontWeight: 700, fontFamily: 'inherit',
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                    }}>
                    <Clock size={14} /> Odgodi 15min
                  </button>
                  <button
                    onClick={() => handleAlertSnooze(60)}
                    disabled={snoozing}
                    style={{
                      flex: '1 1 100px', padding: '11px 14px', borderRadius: 10,
                      background: '#FFFFFF', border: '1.5px solid #E8D4A8',
                      color: '#78510A', cursor: 'pointer',
                      fontSize: 13, fontWeight: 700, fontFamily: 'inherit',
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                    }}>
                    <Clock size={14} /> Odgodi 1h
                  </button>
                  <button
                    onClick={handleAlertDismiss}
                    disabled={snoozing}
                    title="Zatvori bez akcije — popup se neće ponoviti u ovoj sesiji"
                    style={{
                      flex: '0 0 auto', padding: '11px 14px', borderRadius: 10,
                      background: 'transparent', border: '1.5px solid #E8E5E0',
                      color: '#78716C', cursor: 'pointer',
                      fontSize: 13, fontWeight: 600, fontFamily: 'inherit',
                    }}>
                    Zatvori
                  </button>
                </div>
              </motion.div>
            </motion.div>
          );
        })()}
      </AnimatePresence>
    </>
  );
}
